import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Schedule,
  HelpOutline,
  CalendarToday,
  Person,
  Business,
  DateRange,
  TrendingDown,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import apiClient, { updateProgetto } from '../services/api';
import ProjectForm from '../components/ProjectForm';

const MESI_SHORT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

interface AllocazioneMese {
  percentuale: number;
  giorni_uomo: number;
  sorgente: string;
}

interface Risorsa {
  risorsa_id: number;
  nome: string;
  cognome: string;
  struttura: string | null;
  costo_orario: number;
  mesi: Record<string, AllocazioneMese>;
  totale_giorni: number;
  totale_costo: number;
}

interface AllocazioneMensile {
  anno: number;
  mese: number;
  key: string;
  giorni_uomo: number;
}

interface Progetto {
  id: number;
  titolo: string;
  cliente: string | null;
  cliente_finale: string | null;
  pm: string | null;
  commerciale: string | null;
  data_inizio: string | null;
  data_fine: string | null;
  budget_gg_uu: number | null;
  budget_residuo_gg_uu: number | null;
  budget_pianificato_gg_uu: number | null;
  tipologia: string | null;
  id_hrp: number | null;
  descrizione: string | null;
}

interface DetailData {
  progetto: Progetto;
  team: Risorsa[];
  allocazioni_mensili: AllocazioneMensile[];
  dataInizio: string;
  dataFine: string;
  stats: {
    num_risorse: number;
    totale_giorni_periodo: number;
    rendicontato_periodo: number;
    totale_costo_periodo: number;
  };
}

const STATO_CONFIG: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info'; icon: React.ReactElement }> = {
  in_corso: { label: 'In corso', color: 'success', icon: <CheckCircle fontSize="small" /> },
  attenzione: { label: 'Attenzione', color: 'warning', icon: <Warning fontSize="small" /> },
  critico: { label: 'Critico', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  completato: { label: 'Completato', color: 'default', icon: <CheckCircle fontSize="small" /> },
  futuro: { label: 'Futuro', color: 'info', icon: <Schedule fontSize="small" /> },
  sconosciuto: { label: 'N/D', color: 'default', icon: <HelpOutline fontSize="small" /> },
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatGG(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toFixed(1)} gg`;
}

function formatEuro(v: number): string {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

function calcolaStato(p: Progetto): string {
  const now = new Date();
  const dataFine = p.data_fine ? new Date(p.data_fine) : null;
  const dataInizio = p.data_inizio ? new Date(p.data_inizio) : null;
  if (dataFine && dataFine < now) return 'completato';
  if (dataInizio && dataInizio > now) return 'futuro';
  const budgetTot = p.budget_gg_uu || 0;
  const residuo = p.budget_residuo_gg_uu || 0;
  const perc = budgetTot > 0 ? (residuo / budgetTot) * 100 : 100;
  if (perc < 10) return 'critico';
  if (perc < 25) return 'attenzione';
  return 'in_corso';
}

function calcolaTimeline(p: Progetto): number {
  if (!p.data_inizio || !p.data_fine) return 0;
  const now = new Date();
  const start = new Date(p.data_inizio);
  const end = new Date(p.data_fine);
  const durata = end.getTime() - start.getTime();
  const trascorso = now.getTime() - start.getTime();
  return durata > 0 ? Math.max(0, Math.min(100, Math.round((trascorso / durata) * 100))) : 0;
}

function cellColor(perc: number | undefined): string {
  if (!perc || perc === 0) return 'transparent';
  if (perc > 80) return '#c8e6c9';
  if (perc > 40) return '#e8f5e9';
  return '#f1f8e9';
}

const PMProgettoDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const currentYear = new Date().getFullYear();
  const [dataInizio, setDataInizio] = useState<string>(
    searchParams.get('dataInizio') || `${currentYear}-01-01`
  );
  const [dataFine, setDataFine] = useState<string>(
    searchParams.get('dataFine') || `${currentYear}-12-31`
  );
  // Stati locali per l'input: l'API si aggiorna solo su blur o Enter
  const [inputDataInizio, setInputDataInizio] = useState<string>(
    searchParams.get('dataInizio') || `${currentYear}-01-01`
  );
  const [inputDataFine, setInputDataFine] = useState<string>(
    searchParams.get('dataFine') || `${currentYear}-12-31`
  );

  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editFormOpen, setEditFormOpen] = useState(false);

  useEffect(() => {
    const carica = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await apiClient.get(`/pm-dashboard/progetti/${id}?dataInizio=${dataInizio}&dataFine=${dataFine}`);
        setData(resp.data);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Errore nel caricamento del progetto');
      } finally {
        setLoading(false);
      }
    };
    carica();
  }, [id, dataInizio, dataFine]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!data) return null;

  const { progetto, team, allocazioni_mensili, stats } = data;
  const stato = calcolaStato(progetto);
  const cfg = STATO_CONFIG[stato] || STATO_CONFIG.sconosciuto;
  const percTimeline = calcolaTimeline(progetto);
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const budgetTot = progetto.budget_gg_uu || 0;
  const rendicontatoPeriodo = stats.rendicontato_periodo;
  const residuoPeriodo = budgetTot - rendicontatoPeriodo;
  const costiAFinire = allocazioni_mensili
    .filter(m => m.anno > currentYear || (m.anno === currentYear && m.mese > currentMonth))
    .reduce((sum, m) => sum + m.giorni_uomo, 0);

  // Multi-year: determina se il periodo copre più anni
  const annoInizioN = dataInizio ? parseInt(dataInizio.split('-')[0]) : currentYear;
  const annoFineN = dataFine ? parseInt(dataFine.split('-')[0]) : currentYear;
  const multiYear = annoInizioN !== annoFineN;

  const mesLabel = (entry: AllocazioneMensile): string =>
    multiYear
      ? `${MESI_SHORT[entry.mese - 1]} '${String(entry.anno).slice(-2)}`
      : MESI_SHORT[entry.mese - 1];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          size="small"
          onClick={() => navigate('/pm-dashboard')}
          sx={{ mt: 0.5 }}
        >
          Dashboard PM
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h4" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
              {progetto.titolo}
            </Typography>
            <Chip icon={cfg.icon} label={cfg.label} color={cfg.color} />
            {progetto.id_hrp && <Chip label={`HRP #${progetto.id_hrp}`} size="small" variant="outlined" />}
            {progetto.tipologia && <Chip label={progetto.tipologia} size="small" variant="outlined" />}
          </Box>
          <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
            {(progetto.cliente_finale || progetto.cliente) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Business sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary">
                  {progetto.cliente_finale || progetto.cliente}
                </Typography>
              </Box>
            )}
            {progetto.pm && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Person sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary">{progetto.pm}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                {formatDate(progetto.data_inizio)} → {formatDate(progetto.data_fine)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Pulsante modifica */}
        <Tooltip title="Modifica progetto">
          <IconButton
            color="primary"
            onClick={() => setEditFormOpen(true)}
            sx={{ mt: 0.5, flexShrink: 0 }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>

        {/* Selettore periodo */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            type="date"
            label="Da"
            value={inputDataInizio}
            onChange={e => setInputDataInizio(e.target.value)}
            onBlur={e => setDataInizio(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') setDataInizio((e.target as HTMLInputElement).value); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 155 }}
          />
          <TextField
            size="small"
            type="date"
            label="A"
            value={inputDataFine}
            onChange={e => setInputDataFine(e.target.value)}
            onBlur={e => setDataFine(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') setDataFine((e.target as HTMLInputElement).value); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 155 }}
          />
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: 'Budget progetto',
            value: budgetTot > 0 ? formatGG(budgetTot) : 'Non definito',
            sub: budgetTot > 0 ? 'budget totale progetto' : 'budget_gg_uu non impostato',
            color: '#1565c0',
            icon: <DateRange />,
          },
          {
            label: 'Rendicontato periodo',
            value: formatGG(rendicontatoPeriodo),
            sub: budgetTot > 0 ? `${Math.round((rendicontatoPeriodo / budgetTot) * 100)}% del budget` : 'da HRP (sorgente reale)',
            color: budgetTot > 0 && rendicontatoPeriodo > budgetTot ? '#c62828' : '#2e7d32',
            icon: <CheckCircle />,
          },
          {
            label: 'Residuo periodo',
            value: budgetTot > 0 ? formatGG(residuoPeriodo) : '—',
            sub: budgetTot > 0 ? `${Math.round((residuoPeriodo / budgetTot) * 100)}% del budget rimasto` : 'budget non definito',
            color: residuoPeriodo < 0 ? '#c62828' : residuoPeriodo < budgetTot * 0.1 ? '#e65100' : '#1565c0',
            icon: <TrendingDown />,
          },
          {
            label: 'Costi a finire',
            value: formatGG(costiAFinire),
            sub: annoFineN < currentYear
              ? 'periodo già concluso'
              : annoInizioN > currentYear
                ? 'tutto nel futuro'
                : currentMonth < 12 ? `da ${MESI_SHORT[currentMonth]} ${currentYear}` : 'fine anno',
            color: '#7b1fa2',
            icon: <Schedule />,
          },
        ].map(kpi => (
          <Grid item xs={6} md={3} key={kpi.label}>
            <Card sx={{ borderLeft: `4px solid ${kpi.color}`, height: '100%' }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ color: kpi.color, display: 'flex' }}>{kpi.icon}</Box>
                  <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold" sx={{ color: kpi.color }}>
                  {kpi.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">{kpi.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }} alignItems="stretch">
        {/* Timeline */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Avanzamento temporale
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">{formatDate(progetto.data_inizio)}</Typography>
                <Typography variant="caption" fontWeight="bold">{percTimeline}% trascorso</Typography>
                <Typography variant="caption" color="text.secondary">{formatDate(progetto.data_fine)}</Typography>
              </Box>
              <Box sx={{ position: 'relative', height: 28, borderRadius: 14, bgcolor: 'action.hover', overflow: 'visible' }}>
                <Box sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${percTimeline}%`,
                  bgcolor: '#1976d2',
                  borderRadius: 14,
                  transition: 'width 0.5s'
                }} />
                {/* Marker oggi */}
                <Tooltip title="Oggi">
                  <Box sx={{
                    position: 'absolute',
                    left: `${percTimeline}%`,
                    top: -5,
                    transform: 'translateX(-50%)',
                    width: 6,
                    height: 38,
                    bgcolor: '#d32f2f',
                    borderRadius: 3,
                    zIndex: 1
                  }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                <Typography variant="body2" fontWeight="bold" color="error">
                  Oggi: {new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Budget breakdown */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Budget — Periodo
              </Typography>
              {budgetTot > 0 ? (
                <>
                  {/* Barra visuale budget / rendicontato / residuo */}
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Tooltip title={`Rendicontato: ${formatGG(rendicontatoPeriodo)} / Budget: ${formatGG(budgetTot)}`}>
                      <Box sx={{ position: 'relative', height: 20, borderRadius: 10, bgcolor: 'grey.200', overflow: 'hidden' }}>
                        <Box sx={{
                          position: 'absolute',
                          left: 0, top: 0,
                          height: '100%',
                          width: `${Math.min(100, Math.max(0, (rendicontatoPeriodo / budgetTot) * 100))}%`,
                          bgcolor: rendicontatoPeriodo > budgetTot ? '#c62828' : '#2e7d32',
                          borderRadius: 10,
                          transition: 'width 0.5s',
                        }} />
                      </Box>
                    </Tooltip>
                  </Box>
                  {/* Legenda */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 2, bgcolor: rendicontatoPeriodo > budgetTot ? '#c62828' : '#2e7d32', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary">Rendicontato periodo</Typography>
                      </Box>
                      <Typography variant="caption" fontWeight="bold">
                        {formatGG(rendicontatoPeriodo)} ({Math.round((rendicontatoPeriodo / budgetTot) * 100)}%)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 2, bgcolor: 'grey.300', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary">Residuo periodo</Typography>
                      </Box>
                      <Typography variant="caption" fontWeight="bold" sx={{ color: residuoPeriodo < 0 ? 'error.main' : 'inherit' }}>
                        {formatGG(residuoPeriodo)} ({Math.round((residuoPeriodo / budgetTot) * 100)}%)
                      </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">Budget totale</Typography>
                      <Typography variant="caption" fontWeight="bold">{formatGG(budgetTot)}</Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">Budget totale non definito</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Grafico carico mensile */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Carico mensile team — periodo (gg/uomo)
          </Typography>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={allocazioni_mensili} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="key"
                tickFormatter={(k) => {
                  const parts = String(k).split('_');
                  const label = MESI_SHORT[parseInt(parts[1]) - 1] || '';
                  return multiYear ? `${label} '${parts[0].slice(-2)}` : label;
                }}
                tick={{ fontSize: 12 }}
              />
              <YAxis tick={{ fontSize: 12 }} unit=" gg" />
              <RechartsTooltip
                formatter={(value: number) => [`${value.toFixed(1)} gg`, 'Giorni/uomo']}
                labelFormatter={(k) => {
                  const parts = String(k).split('_');
                  return `${MESI_SHORT[parseInt(parts[1]) - 1]} ${parts[0]}`;
                }}
              />
              <Bar dataKey="giorni_uomo" radius={[4, 4, 0, 0]}>
                {allocazioni_mensili.map(entry => (
                  <Cell
                    key={`cell-${entry.key}`}
                    fill={entry.giorni_uomo > 0 ? '#1976d2' : '#e0e0e0'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Tabella team */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Allocazione team per mese — periodo
          </Typography>
          {team.length === 0 ? (
            <Alert severity="info">Nessuna allocazione registrata per il periodo selezionato.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 160, bgcolor: 'grey.50' }}>Risorsa</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>Struttura</TableCell>
                    {allocazioni_mensili.map(m => (
                      <TableCell
                        key={m.key}
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          bgcolor: m.mese === currentMonth && m.anno === currentYear ? '#e3f2fd' : 'grey.50',
                          minWidth: 56,
                          fontSize: 12,
                        }}
                      >
                        {mesLabel(m)}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.50', minWidth: 80 }}>Totale</TableCell>
                    {stats.totale_costo_periodo > 0 && (
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.50', minWidth: 100 }}>Costo</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {team.map(risorsa => (
                    <TableRow key={risorsa.risorsa_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {risorsa.cognome} {risorsa.nome}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {risorsa.struttura || '—'}
                        </Typography>
                      </TableCell>
                      {allocazioni_mensili.map(m => {
                        const alloc = risorsa.mesi[m.key];
                        return (
                          <TableCell
                            key={m.key}
                            align="center"
                            sx={{
                              bgcolor: cellColor(alloc?.percentuale),
                              borderLeft: m.mese === currentMonth && m.anno === currentYear ? '2px solid #90caf9' : undefined,
                            }}
                          >
                            {alloc ? (
                              <Tooltip title={`${alloc.percentuale}% — ${alloc.giorni_uomo} gg${alloc.sorgente === 'H' ? ' (HRP)' : alloc.sorgente === 'E' ? ' (ERP)' : ''}`}>
                                <Typography variant="caption" fontWeight={alloc.percentuale > 60 ? 'bold' : 'normal'}>
                                  {alloc.percentuale}%
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {risorsa.totale_giorni.toFixed(1)} gg
                        </Typography>
                      </TableCell>
                      {stats.totale_costo_periodo > 0 && (
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {formatEuro(risorsa.totale_costo)}
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}

                  {/* Totali colonna */}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                      <Typography variant="body2" fontWeight="bold">Totale</Typography>
                    </TableCell>
                    {allocazioni_mensili.map(m => (
                      <TableCell key={m.key} align="center" sx={{ bgcolor: 'grey.100' }}>
                        {m.giorni_uomo > 0 ? (
                          <Typography variant="caption" fontWeight="bold">
                            {m.giorni_uomo.toFixed(1)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {stats.totale_giorni_periodo.toFixed(1)} gg
                      </Typography>
                    </TableCell>
                    {stats.totale_costo_periodo > 0 && (
                      <TableCell align="right">
                        <Typography variant="caption" fontWeight="bold">
                          {formatEuro(stats.totale_costo_periodo)}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Descrizione */}
      {progetto.descrizione && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Descrizione</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {progetto.descrizione}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Dialog modifica progetto */}
      <ProjectForm
        open={editFormOpen}
        progetto={progetto as any}
        onClose={() => setEditFormOpen(false)}
        onSave={async (formData) => {
          await updateProgetto(progetto.id, formData);
          const resp = await apiClient.get(`/pm-dashboard/progetti/${id}?dataInizio=${dataInizio}&dataFine=${dataFine}`);
          setData(resp.data);
        }}
      />
    </Box>
  );
};

export default PMProgettoDetailPage;
